import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/Avatar';
import { cn } from '@/lib/utils';

const sizeClasses = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
} as const;

const isDevelopment = import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_PROD !== 'true';
const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'courtsync-local.appspot.com';

/** Generate deterministic background color from name for initials fallback */
function getAvatarColor(firstName: string, lastName: string): string {
  const str = `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`.toLowerCase() || '?';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 60%, 45%)`;
}

function buildEmulatorStorageUrl(path: string): string {
  const normalizedPath = path.replace(/^\//, '');
  const encodedPath = encodeURIComponent(normalizedPath);
  return `http://localhost:9199/v0/b/${storageBucket}/o/${encodedPath}?alt=media`;
}

function resolveAvatarUrl(avatarUrl?: string | null): string | undefined {
  if (!avatarUrl) {
    return undefined;
  }

  // Handle raw storage object paths persisted by older/local flows.
  if (avatarUrl.startsWith('/profiles/') || avatarUrl.startsWith('profiles/')) {
    return isDevelopment ? buildEmulatorStorageUrl(avatarUrl) : avatarUrl;
  }

  // In local dev, rewrite production-style Firebase Storage URLs to emulator host.
  if (isDevelopment) {
    try {
      const parsed = new URL(avatarUrl);
      if (parsed.hostname === 'firebasestorage.googleapis.com' && parsed.pathname.startsWith('/v0/b/')) {
        parsed.protocol = 'http:';
        parsed.host = 'localhost:9199';
        return parsed.toString();
      }
    } catch {
      // Keep the original URL when it's not a valid absolute URL.
    }
  }

  return avatarUrl;
}

export interface UserAvatarProps {
  avatarUrl?: string | null;
  firstName?: string;
  lastName?: string;
  size?: keyof typeof sizeClasses;
  className?: string;
  /** Display name for entities without first/last (e.g. child students) */
  displayName?: string;
}

/**
 * Reusable avatar component that shows image or initials fallback.
 * Used for users, coaches with uid, and students (initials only for child students).
 */
export function UserAvatar({ avatarUrl, firstName, lastName, size = 'md', className, displayName }: UserAvatarProps) {
  const resolvedAvatarUrl = resolveAvatarUrl(avatarUrl);
  const initials = (() => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (displayName) {
      const parts = displayName.trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
      return parts[0]?.charAt(0)?.toUpperCase() || '?';
    }
    return '?';
  })();

  const bgColor = getAvatarColor(firstName || '', lastName || displayName || '');

  return (
    <Avatar className={cn(sizeClasses[size], className)}>
      {resolvedAvatarUrl && <AvatarImage src={resolvedAvatarUrl} alt={`${firstName || ''} ${lastName || ''}`.trim() || 'Avatar'} />}
      <AvatarFallback
        className="text-white font-medium"
        style={{ backgroundColor: bgColor }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
